import type { TimeBlock } from '../types';
import {
  computeDay,
  DEFAULT_PRAYER_CONFIG,
  type PrayerKey,
  type PrayerTiming,
  type VoluntaryPrayer,
  type VoluntaryPrayerKey,
} from './dayCycle';
import type { UserLocation, PrayerSettings } from '../stores/locationStore';
import { prayerConfigFromSettings, voluntaryOverridesFromSettings } from '../stores/locationStore';
import { minToHHMM } from './templateSchedule';

/**
 * Prayer time-blocks — the bridge between the pure `dayCycle` engine and the
 * day-template editor / schedule.
 *
 * A prayer block is a special `TimeBlock` carrying a `prayer` key (fajr…isha)
 * instead of a fixed time. It is stored in a template as a RULE; its concrete
 * start/end are resolved per applied day from `computeDay(date, location)` so a
 * single template stays correct across the whole year (prayers drift daily).
 *
 * Blocks are linked to ONE "Prayers" count-habit (targetPerDay = 5, Existential)
 * so completing any prayer block increments the day's prayer count.
 */

/** App-managed prayer habit kinds (mirrors `Habit.system`). */
export type SystemHabitKind = 'prayers' | 'duha' | 'qiyam';

/** Default (restorable) name per managed prayer habit. */
export const SYSTEM_HABIT_DEFAULT_NAME: Record<SystemHabitKind, string> = {
  prayers: 'The 5 Prayers',
  duha: 'Duha',
  qiyam: 'Qiyam',
};

export const PRAYERS_HABIT_NAME = SYSTEM_HABIT_DEFAULT_NAME.prayers;
/** Prayer blocks + the Prayers habit live in the Existential domain. */
export const PRAYER_DOMAIN_ID = 'existential';
export const PRAYERS_PER_DAY = 5;
/** Existential domain colour — overridden from the category store on save/apply. */
export const PRAYER_BLOCK_COLOR = '#ec4899';

/** Canonical daily order. */
export const PRAYER_ORDER: PrayerKey[] = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

/** Display name per prayer key (Fajr…Isha) — these double as the habit's slot ids. */
export const PRAYER_LABEL_BY_KEY: Record<PrayerKey, string> = DEFAULT_PRAYER_CONFIG.reduce(
  (acc, c) => {
    acc[c.key] = c.name;
    return acc;
  },
  {} as Record<PrayerKey, string>
);

/** The 5 prayer names in order — the Prayers habit's `intervalLabels`. */
export const PRAYERS_HABIT_LABELS: string[] = PRAYER_ORDER.map((k) => PRAYER_LABEL_BY_KEY[k]);

/** The named slot a prayer block completes on the Prayers habit. */
export const prayerLabel = (key: PrayerKey): string => PRAYER_LABEL_BY_KEY[key];

export const isPrayerBlock = (b: TimeBlock): boolean => !!b.prayer;
export const isVoluntaryBlock = (b: TimeBlock): boolean => !!b.voluntary;
/** Either an obligatory or a voluntary prayer block (both overlap-exempt + dynamic). */
export const isPrayerLikeBlock = (b: TimeBlock): boolean => !!b.prayer || !!b.voluntary;

/** Stable per-prayer templateBlockId so re-saves/applies preserve the slot. */
export const prayerTemplateBlockId = (key: PrayerKey): string => `__prayer_${key}__`;

/** Voluntary keys in day order (Duha forenoon → Qiyam pre-dawn). */
export const VOLUNTARY_ORDER: VoluntaryPrayerKey[] = ['duha', 'qiyam'];

/** Canonical habit/display name per voluntary key. */
export const VOLUNTARY_NAME_BY_KEY: Record<VoluntaryPrayerKey, string> = { duha: 'Duha', qiyam: 'Qiyam' };

/** Stable per-voluntary templateBlockId (mirrors prayerTemplateBlockId). */
export const voluntaryTemplateBlockId = (key: VoluntaryPrayerKey): string => `__voluntary_${key}__`;

/** Resolve the day cycle for a calendar day string, anchored at noon-UTC (DST-safe). */
function cycleForDate(location: UserLocation, prayer: PrayerSettings, dateKey: string) {
  return computeDay({
    location,
    date: new Date(`${dateKey}T12:00:00Z`),
    prayer: true,
    ramadan: prayer.ramadan,
    fajrAngle: prayer.fajrAngle,
    prayerConfig: prayerConfigFromSettings(prayer),
    // Voluntary prayers are computed only when enabled in settings.
    duha: prayer.duha,
    qiyam: prayer.qiyam,
    duhaDurationMin: prayer.duhaDurationMin,
    qiyamDurationMin: prayer.qiyamDurationMin,
    ...voluntaryOverridesFromSettings(prayer),
  });
}

/** Map a resolved PrayerTiming → wall-clock start/end (HH:MM), or null if unavailable. */
function timingToSpan(t: PrayerTiming): { startTime: string; endTime: string } | null {
  if (!t.available || t.adhanMinutes == null || t.endMinutes == null) return null;
  return { startTime: minToHHMM(t.adhanMinutes), endTime: minToHHMM(t.endMinutes) };
}

/** Map a resolved VoluntaryPrayer → wall-clock start/end (HH:MM), or null if unavailable. */
function voluntaryToSpan(v: VoluntaryPrayer): { startTime: string; endTime: string } | null {
  if (!v.available || v.startMinutes == null || v.endMinutes == null) return null;
  return { startTime: minToHHMM(v.startMinutes), endTime: minToHHMM(v.endMinutes) };
}

interface BuildPrayerBlocksArgs {
  location: UserLocation;
  prayer: PrayerSettings;
  /** Calendar day (YYYY-MM-DD) to resolve times for — a snapshot for display/storage. */
  dateKey: string;
  /** The linked "Prayers" habit id (so each block completes it). */
  habitId: string;
}

/**
 * Build the 5 prayer blocks for a template, resolved for `dateKey` (a snapshot —
 * they're re-resolved per applied day). Unavailable prayers (high-latitude edge)
 * are skipped. Each block carries the `prayer` rule + a stable templateBlockId.
 */
export function buildPrayerBlocks({ location, prayer, dateKey, habitId }: BuildPrayerBlocksArgs): TimeBlock[] {
  let prayers: PrayerTiming[] | null = null;
  try {
    prayers = cycleForDate(location, prayer, dateKey).prayers;
  } catch {
    prayers = null;
  }
  if (!prayers) return [];

  const byKey = new Map(prayers.map((p) => [p.key, p]));
  const blocks: TimeBlock[] = [];
  for (const key of PRAYER_ORDER) {
    const timing = byKey.get(key);
    if (!timing) continue;
    const span = timingToSpan(timing);
    if (!span) continue;
    blocks.push({
      id: crypto.randomUUID(),
      templateBlockId: prayerTemplateBlockId(key),
      name: timing.name,
      startTime: span.startTime,
      endTime: span.endTime,
      category: PRAYER_DOMAIN_ID,
      color: PRAYER_BLOCK_COLOR,
      type: 'habit',
      habitIds: habitId ? [habitId] : [],
      prayer: key,
    });
  }
  return blocks;
}

interface BuildVoluntaryBlocksArgs {
  location: UserLocation;
  prayer: PrayerSettings;
  /** Calendar day (YYYY-MM-DD) to resolve times for — a snapshot for display/storage. */
  dateKey: string;
  /** The voluntary keys to emit blocks for (each must be enabled in settings). */
  keys: VoluntaryPrayerKey[];
  /** Per-key linked habit id (each voluntary prayer has its own 1×/day habit). */
  habitIdByKey: Partial<Record<VoluntaryPrayerKey, string>>;
}

/**
 * Build the requested voluntary (Duha/Qiyam) prayer blocks for a template,
 * resolved for `dateKey` (a snapshot — re-resolved per applied day). A key is
 * skipped when it isn't enabled in settings or is unavailable that day. Each
 * block links to that voluntary prayer's own habit.
 */
export function buildVoluntaryBlocks({
  location,
  prayer,
  dateKey,
  keys,
  habitIdByKey,
}: BuildVoluntaryBlocksArgs): TimeBlock[] {
  const wanted = keys.filter((k) => (k === 'duha' ? prayer.duha : prayer.qiyam));
  if (wanted.length === 0) return [];

  let voluntary: VoluntaryPrayer[] = [];
  try {
    voluntary = cycleForDate(location, prayer, dateKey).voluntary;
  } catch {
    voluntary = [];
  }
  const byKey = new Map(voluntary.map((v) => [v.key, v]));

  const blocks: TimeBlock[] = [];
  for (const key of VOLUNTARY_ORDER) {
    if (!wanted.includes(key)) continue;
    const timing = byKey.get(key);
    const span = timing ? voluntaryToSpan(timing) : null;
    if (!span) continue;
    const habitId = habitIdByKey[key];
    blocks.push({
      id: crypto.randomUUID(),
      templateBlockId: voluntaryTemplateBlockId(key),
      name: timing!.name,
      startTime: span.startTime,
      endTime: span.endTime,
      category: PRAYER_DOMAIN_ID,
      color: PRAYER_BLOCK_COLOR,
      type: 'habit',
      habitIds: habitId ? [habitId] : [],
      voluntary: key,
    });
  }
  return blocks;
}

/**
 * Resolve a day's blocks for `dateKey`: prayer blocks get their times recomputed
 * from `computeDay` for that exact day (and are dropped when the prayer is
 * unavailable or prayer/location config is missing); every other block passes
 * through untouched. This is the single point that turns the stored prayer RULE
 * into concrete times — call it wherever a template's blocks become a day's blocks.
 */
export function resolveDayBlocks(
  blocks: TimeBlock[],
  dateKey: string,
  location: UserLocation | null,
  prayer: PrayerSettings
): TimeBlock[] {
  const hasPrayerLike = blocks.some(isPrayerLikeBlock);
  if (!hasPrayerLike) return blocks;

  let byKey: Map<PrayerKey, PrayerTiming> | null = null;
  let voluntaryByKey: Map<VoluntaryPrayerKey, VoluntaryPrayer> | null = null;
  if (location && prayer.enabled) {
    try {
      const cycle = cycleForDate(location, prayer, dateKey);
      if (cycle.prayers) byKey = new Map(cycle.prayers.map((p) => [p.key, p]));
      voluntaryByKey = new Map(cycle.voluntary.map((v) => [v.key, v]));
    } catch {
      byKey = null;
      voluntaryByKey = null;
    }
  }

  const out: TimeBlock[] = [];
  for (const block of blocks) {
    if (block.prayer) {
      const timing = byKey?.get(block.prayer);
      const span = timing ? timingToSpan(timing) : null;
      if (!span) continue; // prayer disabled / no location / unavailable that day → drop
      out.push({ ...block, startTime: span.startTime, endTime: span.endTime });
      continue;
    }
    if (block.voluntary) {
      // Drop when its voluntary prayer is turned off in settings, or unavailable that day.
      const enabled = block.voluntary === 'duha' ? prayer.duha : prayer.qiyam;
      const timing = enabled ? voluntaryByKey?.get(block.voluntary) : undefined;
      const span = timing ? voluntaryToSpan(timing) : null;
      if (!span) continue;
      out.push({ ...block, startTime: span.startTime, endTime: span.endTime });
      continue;
    }
    out.push(block);
  }
  return out;
}
